import { ReactElement, useEffect } from "react";

// Third parties
import { useDispatch } from "react-redux";

// Evergiving
import { FormConfig } from "@evergiving/common/dist/es2015/form";
import { FormSourceRefKind } from "@evergiving/common/dist/es2015/pledge";

// Redux
import {
  setFormSourceRef,
  setFormPledgePaymentAuthorization,
} from "@/lib/redux/actions";

// Pages
import { PledgePaymentIndexPage } from "@/lib/pages/PledgePaymentIndexPage";
import { DonationIndexPage } from "@/lib/pages/DonationIndexPage";
import { CaptureLeadsIndexPage } from "@/lib/pages/CaptureLeadsIndexPage";
import { ExpiredLinkPage } from "@/lib/pages/ExpiredLinkPage";
import { authorizationIsSatisfiable } from "@/lib/misc/authorizedPaymentSource";
import { useFormRouter } from "../hooks/useFormRouter";

interface IndexPageProps {
  formConfig: FormConfig;
}

export const IndexPage = ({ formConfig }: IndexPageProps): ReactElement => {
  const dispatch = useDispatch();
  const formRouter = useFormRouter();
  const formLink = formRouter.getFormLink();
  const resolved = formLink.kind === "resolved" ? formLink : undefined;

  useEffect(() => {
    if (resolved?.ref) {
      dispatch(setFormSourceRef(resolved.ref));
    }

    if (resolved?.authorization) {
      dispatch(setFormPledgePaymentAuthorization(resolved.authorization));
    }
  }, [dispatch, resolved]);

  // A link that named something and failed, or one authorising a payment source
  // this form cannot offer. Both leave the donor nothing to do here, so both
  // say "no longer valid" rather than rendering an empty payment section.
  if (
    formLink.kind === "invalid" ||
    !authorizationIsSatisfiable(
      formConfig,
      resolved?.authorization?.authorization
    )
  ) {
    return <ExpiredLinkPage formConfig={formConfig} />;
  }

  if ("payment" in formConfig) {
    if (resolved?.ref.kind === FormSourceRefKind.PledgeRef) {
      return <PledgePaymentIndexPage formConfig={formConfig} />;
    }

    return <DonationIndexPage formConfig={formConfig} />;
  }

  return <CaptureLeadsIndexPage formConfig={formConfig} />;
};
